/**
 * AB#21966 / AB#21967 — Team chat on the order.
 *
 * One internal chat per order between CSMs and order admins; the customer and
 * the factory never reach it. The chat polls while it is open (every few
 * seconds, only what is new); the unread inbox polls once a minute for the
 * header bell and the order's Team chat button.
 */
import { computed, reactive, ref } from 'vue';
import { http } from '@/interceptors/axiosRequestInterceptor';

export interface TcPerson {
  id: number;
  name: string;
  initials: string;
  role: string | null;
  avatar_url?: string | null;
}

export interface TcFile {
  id: number;
  name: string;
  size: number;
  mime: string | null;
  is_image: boolean;
  message_id?: number | null;
  by?: string | null;
  at?: string | null;
}

export type TcFlagKind = 'other_order' | 'personal' | 'harsh_tone' | 'sensitive_data';

export interface TcFlag {
  kind: TcFlagKind;
  finding_id?: number;
  subtype?: string;
  masked?: boolean;
  other_order_id?: number | null;
  other_order_no?: string | null;
  dismissed?: boolean;
}

export interface TcElsa {
  finding_id: number;
  kind: TcFlagKind;
  other_order_id: number | null;
  other_order_no: string | null;
  message_count: number;
  reason: string | null;
  resolved: 'moved' | 'dismissed' | null;
  resolved_by?: string;
}

export interface TcMessage {
  id: number;
  kind: 'user' | 'elsa' | 'system';
  user: TcPerson | null;
  body: string | null;
  body_en: string | null;
  source_language: string | null;
  mentions: number[];
  reply_to: { id: number; name: string; excerpt: string } | null;
  portion_id: number | null;
  files: TcFile[];
  flags: TcFlag[];
  elsa: TcElsa | null;
  moved_from: { order_id: number; order_no: string; by: string } | null;
  edited: boolean;
  deleted: boolean;
  created_at: string;
  can_edit: boolean;
  can_delete: boolean;
}

export interface TcMember extends Omit<TcPerson, 'id'> {
  user_id: number;
  following: boolean;
  online: boolean;
  last_read_message_id: number;
}

export interface TcPortion {
  id: number;
  item_no: string;
  factory: string | null;
}

export interface TcState {
  order_id: number;
  order_no: string;
  messages: TcMessage[];
  has_more: boolean;
  latest_id: number;
  members: TcMember[];
  me: { following: boolean; last_read_message_id: number };
  unread: number;
  typing: string[];
  files_count: number;
  portions: TcPortion[];
  can: { send: boolean; manage: boolean };
}

export interface TcInboxItem {
  order_id: number;
  order_item_id: number | null;
  order_no: string;
  company: string | null;
  unread: number;
  mentions: number;
  last: {
    by: string;
    initials: string;
    avatar_url?: string | null;
    kind: string;
    text: string;
    at: string;
  } | null;
}

export type TcAssistAction =
  'write' | 'clearer' | 'shorter' | 'friendlier' | 'formal' | 'fix' | 'translate';

export const REWRITE_OPTIONS: { action: TcAssistAction; label: string }[] = [
  { action: 'clearer', label: 'Clearer' },
  { action: 'shorter', label: 'Shorter' },
  { action: 'friendlier', label: 'Friendlier' },
  { action: 'formal', label: 'More formal' },
  { action: 'fix', label: 'Fix spelling & grammar' },
];

export const FLAG_LABELS: Record<TcFlagKind, string> = {
  other_order: 'Another order',
  personal: 'Personal',
  harsh_tone: 'Tone',
  sensitive_data: 'Sensitive data',
};

const errorText = (e: unknown, fallback: string): string => {
  const data = (
    e as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } }
  )?.response?.data;
  const first = data?.errors ? Object.values(data.errors)[0]?.[0] : undefined;
  return first || data?.message || fallback;
};

/** Looks like English? The same rough rule the backend uses to offer Translate. */
const ENGLISH = new Set(
  'the and is are was were be to of in on for with that this it we you they have has not but from will would can could should please thanks thank there their our your about when what which all any need send sent order delivery'.split(
    ' '
  )
);
export const looksNonEnglish = (text: string): boolean => {
  if (/[Ѐ-ӿ֐-׿؀-ۿ฀-๿぀-ヿ一-鿿가-힯]/.test(text)) return true;
  const words = text
    .toLowerCase()
    .replace(/https?:\/\/\S+/g, ' ')
    .split(/[^\p{L}\p{N}]+/u)
    .filter((w) => w.length > 1 && !/\d/.test(w));
  if (words.length < 4) return false;
  const hits = words.filter((w) => ENGLISH.has(w)).length;
  return hits / words.length < 0.12;
};

/** "@Sania Afzal" tokens for the people the message mentions. */
export const mentionIdsIn = (text: string, people: TcPerson[]): number[] =>
  people.filter((p) => text.includes(`@${p.name}`)).map((p) => p.id);

/* ------------------------------------------------------------------ *
 * One order's chat
 * ------------------------------------------------------------------ */

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useTeamChat(orderId: number) {
  const state = ref<TcState | null>(null);
  const loading = ref(false);
  const sending = ref(false);
  const error = ref<string | null>(null);
  const base = `orders/${orderId}/team-chat`;

  const merge = (incoming: TcMessage[]): void => {
    if (!state.value) return;
    const byId = new Map(state.value.messages.map((m) => [m.id, m]));
    for (const m of incoming) byId.set(m.id, m);
    state.value.messages = [...byId.values()].sort((a, b) => a.id - b.id);
  };

  const load = async (): Promise<void> => {
    loading.value = true;
    error.value = null;
    try {
      const { data } = await http.get(base);
      state.value = data.result as TcState;
    } catch (e) {
      error.value = errorText(e, 'The team chat could not be loaded.');
    } finally {
      loading.value = false;
    }
  };

  /** New messages since the newest one shown, plus who is typing and unread. */
  const poll = async (): Promise<void> => {
    if (!state.value) {
      await load();
      return;
    }
    try {
      const after = state.value.messages.reduce((m, x) => Math.max(m, x.id), 0);
      const { data } = await http.get(base, { params: { after_id: after } });
      const r = data.result as TcState;
      merge(r.messages);
      state.value.typing = r.typing;
      state.value.members = r.members;
      state.value.me = r.me;
      state.value.unread = r.unread;
      state.value.files_count = r.files_count;
      state.value.latest_id = r.latest_id;
    } catch {
      /* the next poll tries again */
    }
  };

  /** Edits, removals and ELSA's resolutions change existing rows: reload the page. */
  const refresh = async (): Promise<void> => {
    try {
      const { data } = await http.get(base);
      const r = data.result as TcState;
      if (state.value) {
        const older = state.value.messages.filter((m) => m.id < (r.messages[0]?.id ?? 0));
        state.value = { ...r, messages: [...older, ...r.messages] };
      } else state.value = r;
    } catch {
      /* keep what is shown */
    }
  };

  const loadOlder = async (): Promise<void> => {
    if (!state.value?.has_more) return;
    const before = state.value.messages[0]?.id;
    const { data } = await http.get(base, { params: { before_id: before } });
    const r = data.result as TcState;
    state.value.has_more = r.has_more;
    merge(r.messages);
  };

  const send = async (payload: {
    body: string;
    files?: File[];
    replyToId?: number | null;
    portionId?: number | null;
    mentions?: number[];
  }): Promise<boolean> => {
    sending.value = true;
    error.value = null;
    try {
      const form = new FormData();
      form.append('body', payload.body);
      if (payload.replyToId) form.append('reply_to_id', String(payload.replyToId));
      if (payload.portionId) form.append('order_item_id', String(payload.portionId));
      for (const id of payload.mentions ?? []) form.append('mentions[]', String(id));
      for (const f of payload.files ?? []) form.append('files[]', f);
      const { data } = await http.post(`${base}/messages`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      merge([data.result.message as TcMessage]);
      if (state.value) state.value.me.following = true;
      return true;
    } catch (e) {
      error.value = errorText(e, 'It was not sent. Try again.');
      return false;
    } finally {
      sending.value = false;
    }
  };

  const edit = async (id: number, body: string): Promise<boolean> => {
    try {
      const { data } = await http.put(`${base}/messages/${id}`, { body });
      merge([data.result.message as TcMessage]);
      return true;
    } catch (e) {
      error.value = errorText(e, 'It was not saved.');
      return false;
    }
  };

  const remove = async (id: number): Promise<boolean> => {
    try {
      await http.delete(`${base}/messages/${id}`);
      await refresh();
      return true;
    } catch (e) {
      error.value = errorText(e, 'It was not removed.');
      return false;
    }
  };

  const markRead = async (): Promise<void> => {
    if (!state.value) return;
    const upTo = state.value.messages.reduce((m, x) => Math.max(m, x.id), 0);
    if (!upTo || upTo <= state.value.me.last_read_message_id) return;
    state.value.me.last_read_message_id = upTo;
    state.value.unread = 0;
    try {
      await http.post(`${base}/read`, { up_to: upTo });
      inbox.clearOrder(orderId);
    } catch {
      /* harmless */
    }
  };

  const setFollowing = async (following: boolean): Promise<void> => {
    if (state.value) state.value.me.following = following;
    try {
      await http.post(`${base}/follow`, { following });
    } catch {
      if (state.value) state.value.me.following = !following;
    }
  };

  let lastTyping = 0;
  const typing = (): void => {
    const now = Date.now();
    if (now - lastTyping < 3000) return;
    lastTyping = now;
    http.post(`${base}/typing`).catch(() => undefined);
  };

  const files = async (): Promise<TcFile[]> => {
    const { data } = await http.get(`${base}/files`);
    return data.result.files as TcFile[];
  };

  const fileUrl = async (fileId: number): Promise<string | null> => {
    try {
      const { data } = await http.get(`${base}/files/${fileId}`);
      if (data?.result?.url) return data.result.url as string;
    } catch {
      /* fall through to the stream */
    }
    try {
      const res = await http.get(`${base}/files/${fileId}`, {
        params: { stream: 1 },
        responseType: 'blob',
      });
      return URL.createObjectURL(res.data as Blob);
    } catch {
      return null;
    }
  };

  const translateMessage = async (id: number): Promise<void> => {
    try {
      const { data } = await http.post(`${base}/messages/${id}/translate`);
      const m = state.value?.messages.find((x) => x.id === id);
      if (m) {
        m.body_en = data.result.body_en;
        m.source_language = data.result.source_language;
      }
    } catch (e) {
      error.value = errorText(e, 'It could not be translated.');
    }
  };

  const dismiss = async (findingId: number): Promise<boolean> => {
    try {
      await http.post(`${base}/findings/${findingId}/dismiss`);
      await refresh();
      return true;
    } catch (e) {
      error.value = errorText(e, 'That did not work.');
      return false;
    }
  };

  const move = async (findingId: number, orderNo?: string): Promise<boolean> => {
    try {
      await http.post(`${base}/findings/${findingId}/move`, orderNo ? { order_no: orderNo } : {});
      await refresh();
      return true;
    } catch (e) {
      error.value = errorText(e, 'The messages were not moved.');
      return false;
    }
  };

  const exportUrl = async (): Promise<string | null> => {
    try {
      const res = await http.get(`${base}/export`, { responseType: 'blob' });
      return URL.createObjectURL(res.data as Blob);
    } catch {
      return null;
    }
  };

  const unreadFrom = computed(() => state.value?.me.last_read_message_id ?? 0);

  return {
    state,
    loading,
    sending,
    error,
    unreadFrom,
    load,
    poll,
    refresh,
    loadOlder,
    send,
    edit,
    remove,
    markRead,
    setFollowing,
    typing,
    files,
    fileUrl,
    translateMessage,
    dismiss,
    move,
    exportUrl,
  };
}

/* ------------------------------------------------------------------ *
 * Composer helpers and people
 * ------------------------------------------------------------------ */

export const assist = async (
  action: TcAssistAction,
  text: string,
  orderNo?: string
): Promise<{ text: string; source_language?: string | null }> => {
  try {
    const { data } = await http.post('team-chat/assist', { action, text, order_no: orderNo });
    return data.result;
  } catch (e) {
    throw new Error(errorText(e, 'That did not work just now. Try again.'));
  }
};

export const searchPeople = async (q: string): Promise<TcPerson[]> => {
  try {
    const { data } = await http.get('team-chat/people', { params: { q } });
    return data.result.people as TcPerson[];
  } catch {
    return [];
  }
};

/* ------------------------------------------------------------------ *
 * The unread inbox (one per app)
 * ------------------------------------------------------------------ */

const inboxState = reactive({
  items: [] as TcInboxItem[],
  unread: 0,
  mentions: 0,
  byOrder: {} as Record<number, number>,
  loaded: false,
});
let inboxTimer: ReturnType<typeof setInterval> | null = null;
let inboxUsers = 0;

const loadInbox = async (): Promise<void> => {
  try {
    const { data } = await http.get('team-chat/inbox');
    const r = data.result;
    inboxState.items = r.items ?? [];
    inboxState.unread = r.unread ?? 0;
    inboxState.mentions = r.mentions ?? 0;
    inboxState.byOrder = r.by_order ?? {};
    inboxState.loaded = true;
  } catch {
    /* the next poll tries again */
  }
};

export const inbox = {
  state: inboxState,
  load: loadInbox,
  /** Start polling (reference counted: the bell and the order nav share it). */
  start(pollMs = 60000): void {
    inboxUsers++;
    if (inboxTimer === null) {
      void loadInbox();
      inboxTimer = setInterval(loadInbox, pollMs);
    }
  },
  stop(): void {
    inboxUsers = Math.max(0, inboxUsers - 1);
    if (inboxUsers === 0 && inboxTimer) {
      clearInterval(inboxTimer);
      inboxTimer = null;
    }
  },
  clearOrder(orderId: number): void {
    const n = inboxState.byOrder[orderId] ?? 0;
    if (!n) return;
    inboxState.unread = Math.max(0, inboxState.unread - n);
    const item = inboxState.items.find((i) => i.order_id === orderId);
    if (item) inboxState.mentions = Math.max(0, inboxState.mentions - item.mentions);
    inboxState.byOrder = { ...inboxState.byOrder, [orderId]: 0 };
    inboxState.items = inboxState.items.filter((i) => i.order_id !== orderId);
  },
};

export const ago = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  const day = 86400000;
  const diff = Date.now() - d.getTime();
  if (diff < day && d.getDate() === new Date().getDate())
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (diff < 6 * day)
    return `${d.toLocaleDateString([], { weekday: 'short' })} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  return d.toLocaleDateString([], { day: 'numeric', month: 'short' });
};

export const dayLabel = (iso: string): string => {
  const d = new Date(iso);
  const today = new Date();
  const y = new Date(today);
  y.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === y.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'short' });
};

export const fileSize = (n: number): string =>
  n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
